<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto flex items-center justify-center"
  >
    <div class="bg-white rounded-lg shadow-xl w-11/12 md:w-1/2 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
        <h4 class="text-lg text-gray-800 font-bold">
          Add Service to {{ departmentName }}
        </h4>
        <button
          type="button"
          class="text-gray-500 hover:text-custom-yellow"
          @click="close"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 14 14"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-6">
        <!-- Service Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <span class="text-red-500">*</span> Service Name
          </label>
          <input
            v-model="service.name"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter service name"
          />
          <p v-if="service.errors.name" class="text-red-500 text-sm mt-1">
            {{ service.errors.name }}
          </p>
        </div>

        <!-- Unit Price -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <span class="text-red-500">*</span> Unit Price (KSH)
          </label>
          <input
            v-model="service.unit_price"
            type="number"
            step="0.01"
            min="0"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter price"
          />
          <p v-if="service.errors.unit_price" class="text-red-500 text-sm mt-1">
            {{ service.errors.unit_price }}
          </p>
        </div>

        <!-- Sample Selection (only for Laboratory services) -->
        <div v-if="showSampleField" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Sample Type
          </label>
          <select
            v-model="service.sample_id"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Sample</option>
            <option
              v-for="sample in samples"
              :key="sample.id"
              :value="sample.id"
            >
              {{ sample.name }}
            </option>
          </select>
        </div>

        <!-- Buttons -->
        <div class="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            @click="close"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="showLoader"
            class="px-4 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
          >
            {{ showLoader ? "Adding..." : "Add Service" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "AddServicesModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    departmentId: {
      type: [Number, String],
      required: true,
    },
    departmentName: {
      type: String,
      required: true,
    },
    discipline: { type: String, default: null }
  },
  data() {
    return {
      service: {
        name: "",
        unit_price: "",
        sample_id: "",
        errors: {},
      },
      samples: [],
      showLoader: false,
      showSampleField: false,
    };
  },
  mounted() {
    this.checkIfLaboratory();
    if (this.showSampleField) {
      this.fetchLabSamples();
    }
  },
  watch: {
    departmentId() {
      this.checkIfLaboratory();
    },
  },
  methods: {
    checkIfLaboratory() {
      // Check if this is a laboratory department (code: FST-002)
      this.fetchDepartmentDetails();
    },

    async fetchDepartmentDetails() {
      try {
        const response = await apiClient.getFacilityServiceTypes();
        if (response.success) {
          const department = response.data.find(
            (dept) => dept.id == this.departmentId
          );
          this.showSampleField = department?.code === "FST-002";
        }
      } catch (error) {
        console.error("Failed to fetch department details", error);
      }
    },

    async fetchLabSamples() {
      try {
        const response = await apiClient.getLabSamples();
        if (response.success) {
          this.samples = response.data;
        }
      } catch (error) {
        this.$toast.error("Failed to fetch lab samples");
        console.error(error);
      }
    },

    validateForm() {
      this.service.errors = {};
      let isValid = true;

      if (!this.service.name || !this.service.name.trim()) {
        this.service.errors.name = "Service name is required";
        isValid = false;
      }

      if (!this.service.unit_price || this.service.unit_price <= 0) {
        this.service.errors.unit_price = "Unit price must be greater than 0";
        isValid = false;
      }

      return isValid;
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.showLoader = true;

        const payload = {
          facility_services: [
            {
              discipline: this.discipline,
              service_type_id: { id: this.departmentId },
              name: this.service.name,
              unit_price: this.service.unit_price,
              sample_id: this.service.sample_id || null,
            },
          ],
        };

        const response = await apiClient.createFacilityService(payload);

        if (response.success) {
          this.$toast.success("Service added successfully");
          this.$emit("refetch");
          this.close();
        } else {
          this.$toast.error(response.message || "Failed to add service");
        }
      } catch (error) {
        this.$toast.error("Failed to add service");
        console.error(error);
      } finally {
        this.showLoader = false;
      }
    },

    close() {
      // Reset form
      this.service = {
        name: "",
        unit_price: "",
        sample_id: "",
        errors: {},
      };
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.bg-custom-yellow {
  background-color: #f59e0b;
}
</style>